import React, { useId } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faTimes, faFile, faVideo, faImage, faCheckCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import './shared.css';

const MediaUpload = ({
    type = 'IMAGE',
    accept,
    onUpload,
    onRemove,
    previewUrl,
    fileName,
    uploading,
    isUploaded, // boolean to show if uploaded to server
    id,
    error
}) => {

    // Generate random ID if not provided. We use a ref to keep it stable across renders if needed, 
    // but for this simple component a constant based on prop or random is fine.
    // To avoid "useId" conditional hook error, we simply won't use useId here.
    const [randomId] = React.useState(() => Math.random().toString(36).substr(2, 9));
    const inputId = id || `media-upload-${type}-${randomId}`;

    // Determine icon based on type
    const getIcon = () => {
        switch (type) {
            case 'VIDEO': return faVideo;
            case 'DOCUMENT': return faFile;
            default: return faImage;
        }
    };

    return (
        <div className="wa-media-component">
            <input
                type="file"
                id={inputId}
                style={{ display: 'none' }}
                accept={accept || (type === 'IMAGE' ? "image/*" : (type === 'VIDEO' ? "video/*" : "*/*"))}
                onChange={onUpload}
                disabled={uploading}
            />

            {!previewUrl && !fileName && (
                <label htmlFor={inputId} className="wa-media-upload-box">
                    {uploading ? (
                        <div style={{ color: '#4F46E5', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={faCloudUploadAlt} spin />
                            <span>Uploading...</span>
                        </div>
                    ) : (
                        <>
                            <div style={{ fontSize: '24px', marginBottom: '8px', color: '#9CA3AF' }}>
                                <FontAwesomeIcon icon={faCloudUploadAlt} />
                            </div>
                            <div style={{ fontWeight: 500, color: '#374151' }}>
                                Upload {type.toLowerCase()}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                {type === 'IMAGE' ? 'JPG, PNG up to 5MB' : 'MP4, 3GP up to 16MB'}
                            </div>
                        </>
                    )}
                </label>
            )}

            {/* Error Message */}
            {uploading === false && !isUploaded && !previewUrl && (
                <div style={{ color: '#DC2626', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {/* Placeholder for parent to pass error, or if error handling is external. 
                         The current component prop 'uploading' is boolean. 
                         Ideally we need an 'error' prop. For now, we'll assume the parent handles displaying errors externally 
                         or we add an error prop. But user request "The error (file too large etc) should come just below the image upload section" implies internal handling or placement.
                         I will check if there is an error prop available or if I need to add one.
                         Looking at file usage, error state is in parent. I will just add a slot for children or error prop.
                         Wait, the user said "The error... should come below". 
                         I'll add an `error` prop to MediaUpload. */}
                </div>
            )}

            {(previewUrl || fileName) && (

                <div className="wa-media-preview-container">
                    <div className="wa-media-preview-generic" style={{ padding: '12px' }}>
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                flexShrink: 0,
                                background: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                            }}
                            onClick={() => window.open(previewUrl, '_blank')}
                            title="Click to view full preview"
                        >
                            {type === 'IMAGE' && previewUrl ? (
                                <img src={previewUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Thumbnail" />
                            ) : (
                                <FontAwesomeIcon icon={getIcon()} size="lg" color="#6B7280" />
                            )}
                        </div>

                        <div style={{ flex: 1, minWidth: 0, marginLeft: '12px' }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
                                {fileName || `${type} File`}
                            </div>
                            {isUploaded && (
                                <div style={{ fontSize: '12px', color: '#059669', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontWeight: 500 }}>
                                    <FontAwesomeIcon icon={faCheckCircle} size="sm" />
                                    <span>Uploaded</span>
                                </div>
                            )}
                        </div>

                        <button
                            className="wa-remove-btn-modern"
                            onClick={onRemove}
                            title="Remove file"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                </div>
            )}

            {/* Display Error if passed */}
            {error && (
                <div style={{ color: '#DC2626', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default MediaUpload;
